import { type URLSearchParams } from 'url';

import {
  CrossCircledIcon,
  MagnifyingGlassIcon,
  MixerHorizontalIcon,
  SewingPinFilledIcon,
} from '@radix-ui/react-icons';
import { useRef, useState } from 'react';
import { useSubmit } from 'react-router';
import { z } from 'zod';

import { useClearableField } from '~/components/forms/use-clearable-field';
import { Label } from '~/components/ui/label';
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '~/components/ui/popover';
import { RadioGroup, RadioGroupItem } from '~/components/ui/radio-group';
import { useToast } from '~/components/ui/use-toast';
import { INPUT_NO_FOCUS_HIGHLIGHT_CLASS } from '~/styles/classNames/inputs';
import { cn } from '~/styles/extended-theme';
import { type PropsWithClassName } from '~/utils/props-with-classname';

import { CityCombobox } from './city-combobox';

interface Props {
  namePlaceholder: string;
  comboboxItems: string[];
  searchParams: URLSearchParams;
}

const DISTANCE_OPTIONS = ['5', '10', '25', '50', '100'];
const DEFAULT_DISTANCE = '25';

export const searchInputSchema = z.object({
  search: z.string().trim().optional(),
  city: z.string().trim().optional(),
  lat: z
    .string()
    .optional()
    .transform((v) => (v === undefined ? undefined : parseFloat(v)))
    .pipe(z.number().optional()),
  long: z
    .string()
    .optional()
    .transform((v) => (v === undefined ? undefined : parseFloat(v)))
    .pipe(z.number().optional()),
  dist: z
    .string()
    .optional()
    .transform((v) => (v === undefined ? undefined : parseInt(v, 10)))
    .pipe(z.number().optional()),
});

export function SearchInput({
  namePlaceholder,
  comboboxItems,
  searchParams,
  className,
}: PropsWithClassName<Props>) {
  const submit = useSubmit();
  const toast = useToast();

  const initialName = searchParams.get('search') || '';
  const nameField = useClearableField('search', initialName);

  const initialCity = searchParams.get('city') || '';
  const cityField = useClearableField('city', initialCity);

  const [popoverOpen, setPopoverOpen] = useState(false);
  const currentLocationInputRef = useRef<HTMLInputElement>(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const latitude = searchParams.get('lat') || '';
  const longitude = searchParams.get('long') || '';
  const distance = searchParams.get('dist') || DEFAULT_DISTANCE;
  const searchingByCoordinates = latitude && longitude;

  const handleSearchByCity = (e: React.MouseEvent<HTMLButtonElement>, city: string) => {
    e.preventDefault();
    const form = e.currentTarget.form;
    if (form) {
      const formData = new FormData(form);
      formData.set('city', city);
      formData.delete('lat');
      formData.delete('long');
      formData.delete('dist');
      submit(formData);
    }
  };

  const handleSearchByLocation = (e: React.MouseEvent<HTMLButtonElement>) => {
    setLocationLoading(true);
    cityField.clearWithoutSubmit();
    const form = e.currentTarget.form;
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocationLoading(false);
        if (form) {
          const formData = new FormData(form);
          formData.delete('city');
          formData.set('lat', position.coords.latitude.toString());
          formData.set('long', position.coords.longitude.toString());
          formData.set('dist', distance);
          submit(formData);
        }
      },
      (error) => {
        console.log('Error getting location:', error);
        setLocationLoading(false);
        toast.toast({
          title: 'Error',
          description: 'Location not found.',
        });
      },
    );
  };

  const handleDistanceChange = (value: string) => {
    setPopoverOpen(false);
    const form = currentLocationInputRef.current?.form;
    if (form) {
      const formData = new FormData(form);
      formData.set('lat', latitude);
      formData.set('long', longitude);
      formData.set('dist', value);
      submit(formData);
    }
  };

  const handleClearCoordinates = () => {
    const form = currentLocationInputRef.current?.form;
    if (form) {
      const formData = new FormData(form);
      formData.delete('lat');
      formData.delete('long');
      formData.delete('dist');
      submit(formData);
    }
  };

  return (
    <div
      className={cn(
        'relative inline-flex w-full min-w-max flex-col gap-2 rounded-full border-gray-400 tablet:my-[1px] tablet:w-2/3 tablet:flex-row tablet:border tablet:border-solid desktop:w-1/2',
        'tablet:hover:border-1 tablet:hover:border-primary',
        'tablet:focus-within:my-0 tablet:focus-within:border-2 tablet:focus-within:border-primary',
        className,
      )}
    >
      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <div
          className={cn(
            'my-[1px] flex flex-1 shrink items-center gap-1 rounded-full border border-solid border-gray-400 p-2 tablet:my-0 tablet:border-none tablet:p-0',
            'hover:border-1 focus-within:my-0 focus-within:border-2 focus-within:border-primary hover:border-primary',
            'tablet:focus-within:border-0 tablet:hover:border-0',
          )}
        >
          <div className="flex shrink-0 items-center justify-center">
            <MagnifyingGlassIcon width={16} height={16} />
          </div>
          <input
            type="text"
            id="search"
            className={cn('grow bg-transparent pb-[2px] font-light', INPUT_NO_FOCUS_HIGHLIGHT_CLASS)}
            placeholder={namePlaceholder}
            {...nameField.inputProps}
          />
          <div className="flex w-4 items-center justify-center">
            {nameField.showClearButton ? (
              <button type="button" {...nameField.clearButtonProps}>
                <CrossCircledIcon />
              </button>
            ) : null}
          </div>
        </div>

        <div className="hidden h-6 w-[1px] bg-gray-400 tablet:block" />

        <div className="flex-1">
          <div
            className={cn(
              'my-[1px] flex items-center gap-1 rounded-full border border-solid border-gray-400 p-2 tablet:my-0 tablet:border-none tablet:p-0',
              'hover:border-1 focus-within:my-0 focus-within:border-2 focus-within:border-primary hover:border-primary',
              'tablet:focus-within:border-0 tablet:hover:border-0',
            )}
          >
            <SewingPinFilledIcon className="shrink-0" width={15} height={15} />
            {locationLoading ? (
              <input value="Searching..." disabled className="font-light" />
            ) : searchingByCoordinates ? (
              <>
                <input
                  ref={currentLocationInputRef}
                  value="Current Location"
                  disabled
                  className="grow pb-[2px] font-light"
                />
                <input name="lat" hidden value={latitude} />
                <input name="long" hidden value={longitude} />
                <input name="dist" hidden value={distance || DEFAULT_DISTANCE} />
                <div className="mr-1 w-4">
                  <PopoverTrigger>
                    <MixerHorizontalIcon />
                  </PopoverTrigger>
                </div>
                <div className="flex w-4 items-center justify-center">
                  <button type="button" onClick={handleClearCoordinates}>
                    <CrossCircledIcon />
                  </button>
                </div>

                <PopoverContent
                  align="start"
                  side="bottom"
                  className="w-[320px] bg-primary-foreground text-primary"
                  onCloseAutoFocus={(e) => {
                    e.preventDefault();
                  }}
                >
                  <p className="mb-4 text-subtitle2">Distance</p>
                  <RadioGroup
                    defaultValue={distance}
                    onValueChange={handleDistanceChange}
                    className="gap-3"
                  >
                    {DISTANCE_OPTIONS.map((d) => (
                      <div key={d} className="flex items-center gap-2">
                        <RadioGroupItem id={`distance-${d}`} value={d} />
                        <Label htmlFor={`distance-${d}`} className="text-body2 font-light">
                          {d === DEFAULT_DISTANCE ? `Auto (${d} miles)` : `${d} miles`}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </PopoverContent>
              </>
            ) : (
              <>
                <CityCombobox
                  inputProps={{
                    className: INPUT_NO_FOCUS_HIGHLIGHT_CLASS,
                  }}
                  items={comboboxItems}
                  {...cityField.inputProps}
                  onCurrentLocationClick={handleSearchByLocation}
                  onCityClick={handleSearchByCity}
                  className="grow"
                />

                <div className="flex w-4 items-center justify-center">
                  {cityField.showClearButton ? (
                    <button type="button" {...cityField.clearButtonProps}>
                      <CrossCircledIcon />
                    </button>
                  ) : null}
                </div>
              </>
            )}
          </div>
        </div>
        <PopoverAnchor className="pointer-events-none absolute bottom-0 top-0 mb-[5px] w-full tablet:left-1/2 tablet:mb-[3px]" />
      </Popover>
    </div>
  );
}
